import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api from '../api';

const QuizTaking = ({ quiz, attempt, onFinish }) => {
    const [answers, setAnswers] = useState(attempt.answers || []);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
    const startTimeRef = useRef(Date.now());

    const handleSubmit = useCallback(async (autoSubmit = false) => {
        if (!autoSubmit && !window.confirm('Are you sure you want to submit your answers? You cannot change them after submission.')) {
            setShowConfirmSubmit(false);
            return;
        }

        try {
            setLoading(true);
            const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);

            const res = await api.put(`/quizzes/${quiz._id}/attempt/${attempt._id}`, {
                answers,
                timeSpent
            });

            // Show results if allowed
            if (quiz.showResults) {
                const message = res.data.message
                    ? `${res.data.message}\n\nScore: ${res.data.score}/${res.data.totalPoints} (${res.data.percentage.toFixed(1)}%)`
                    : `Quiz submitted successfully!\nScore: ${res.data.score}/${res.data.totalPoints} (${res.data.percentage.toFixed(1)}%)`;
                alert(message);
            } else {
                alert('Quiz submitted successfully! Results will be available later.');
            }

            onFinish();
        } catch (error) {
            console.error('Error submitting quiz:', error);
            alert('Error submitting quiz. Please try again.');
        } finally {
            setLoading(false);
            setShowConfirmSubmit(false);
        }
    }, [quiz._id, attempt._id, answers, quiz.showResults, onFinish]);

    useEffect(() => {
        // Initialize answers if not already set
        if (!attempt.answers || attempt.answers.length === 0) {
            const initialAnswers = quiz.questions.map(question => ({
                questionId: question._id,
                selectedOptions: [],
                textAnswer: '',
                isCorrect: null,
                pointsEarned: 0
            }));
            setAnswers(initialAnswers);
        }

        // Set up timer if quiz has time limit
        if (quiz.timeLimit) {
            const startTime = new Date(attempt.startedAt).getTime();
            const endTime = startTime + (quiz.timeLimit * 60 * 1000);

            const updateTimer = () => {
                const now = Date.now();
                const remaining = Math.max(0, endTime - now);
                setTimeLeft(remaining);

                if (remaining === 0) {
                    handleSubmit(true); // Auto-submit when time runs out
                }
            };

            updateTimer();
            const timer = setInterval(updateTimer, 1000);
            return () => clearInterval(timer);
        }
    }, [quiz, attempt, handleSubmit]);

    const formatTime = (milliseconds) => {
        const minutes = Math.floor(milliseconds / 60000);
        const seconds = Math.floor((milliseconds % 60000) / 1000);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Component to render text with math support
    const MathRenderer = ({ children, className = '' }) => {
        if (!children) return null;
        return (
            <div className={`prose prose-slate max-w-none ${className}`}>
                <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                >
                    {children}
                </ReactMarkdown>
            </div>
        );
    };

    const handleAnswerChange = (questionIndex, answerUpdate) => {
        const updatedAnswers = [...answers];
        updatedAnswers[questionIndex] = { ...updatedAnswers[questionIndex], ...answerUpdate };
        setAnswers(updatedAnswers);
    };

    const handleOptionToggle = (questionIndex, optionText) => {
        const question = quiz.questions[questionIndex];
        const currentAnswer = answers[questionIndex];

        let newSelectedOptions;
        if (question.questionType === 'multiple-choice') {
            // For multiple choice, allow multiple selections
            if (currentAnswer.selectedOptions.includes(optionText)) {
                newSelectedOptions = currentAnswer.selectedOptions.filter(opt => opt !== optionText);
            } else {
                newSelectedOptions = [...currentAnswer.selectedOptions, optionText];
            }
        } else {
            // For true/false or single choice, only allow one selection
            newSelectedOptions = [optionText];
        }

        handleAnswerChange(questionIndex, { selectedOptions: newSelectedOptions });
    };

    const isAnswered = (questionIndex) => {
        const answer = answers[questionIndex];
        const question = quiz.questions[questionIndex];

        if (question.questionType === 'short-answer' || question.questionType === 'essay') {
            return answer.textAnswer.trim().length > 0;
        } else {
            return answer.selectedOptions.length > 0;
        }
    };

    const getAnsweredCount = () => {
        return answers.filter((_, index) => isAnswered(index)).length;
    };

    const question = quiz.questions[currentQuestion];
    const currentAnswer = answers[currentQuestion];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">{quiz.title}</h1>
                        <p className="text-slate-600 mt-1">
                            Câu {currentQuestion + 1} / {quiz.questions.length}
                        </p>
                    </div>
                    <div className="text-right">
                        {timeLeft !== null && (
                            <div className={`text-lg font-mono ${
                                timeLeft < 300000 ? 'text-red-600' : timeLeft < 600000 ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                                ⏱️ {formatTime(timeLeft)}
                            </div>
                        )}
                        <div className="text-sm text-slate-600">
                            Đã trả lời: {getAnsweredCount()}/{quiz.questions.length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-slate-700">Tiến độ:</span>
                    <span className="text-sm text-slate-600">{Math.round(((currentQuestion + 1) / quiz.questions.length) * 100)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
                    ></div>
                </div>
            </div>

            {/* Question */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="mb-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <MathRenderer className="text-lg font-semibold text-slate-900">
                                {question.questionText}
                            </MathRenderer>
                            {question.questionImage && (
                                <div className="mt-4">
                                    <img
                                        src={`http://localhost:5001${question.questionImage}`}
                                        alt="Question"
                                        className="max-w-full h-auto max-h-64 rounded-lg border border-slate-200 shadow-sm"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="text-sm text-slate-500 ml-4">
                            {question.points} điểm
                        </div>
                    </div>

                    {/* Answer Options */}
                    <div className="space-y-3">
                        {question.questionType === 'multiple-choice' && (
                            <div className="space-y-3">
                                {question.options.map((option, optionIndex) => (
                                    <label key={optionIndex} className="flex items-start space-x-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={currentAnswer.selectedOptions.includes(option.text)}
                                            onChange={() => handleOptionToggle(currentQuestion, option.text)}
                                            className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <div className="text-slate-700 flex-1">
                                            <MathRenderer>{option.text}</MathRenderer>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}

                        {question.questionType === 'true-false' && (
                            <div className="space-y-3">
                                {['true', 'false'].map((option) => (
                                    <label key={option} className="flex items-start space-x-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                                        <input
                                            type="radio"
                                            name={`question_${currentQuestion}`}
                                            checked={currentAnswer.selectedOptions.includes(option)}
                                            onChange={() => handleOptionToggle(currentQuestion, option)}
                                            className="mt-1 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <span className="text-slate-700 flex-1">
                                            {option === 'true' ? 'Đúng' : 'Sai'}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {(question.questionType === 'short-answer' || question.questionType === 'essay') && (
                            <div>
                                <textarea
                                    value={currentAnswer.textAnswer}
                                    onChange={(e) => handleAnswerChange(currentQuestion, { textAnswer: e.target.value })}
                                    placeholder="Nhập câu trả lời của bạn..."
                                    rows={question.questionType === 'essay' ? 6 : 3}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <button
                        onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                        disabled={currentQuestion === 0}
                        className="px-4 py-2 rounded-xl font-medium text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        ← Câu trước
                    </button>

                    <div className="text-sm text-slate-600">
                        {isAnswered(currentQuestion) ? '✅ Đã trả lời' : '⚪ Chưa trả lời'}
                    </div>

                    {currentQuestion < quiz.questions.length - 1 ? (
                        <button
                            onClick={() => setCurrentQuestion(Math.min(quiz.questions.length - 1, currentQuestion + 1))}
                            className="px-4 py-2 rounded-xl font-medium text-sm bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                        >
                            Câu tiếp →
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowConfirmSubmit(true)}
                            disabled={loading}
                            className="px-6 py-2 rounded-xl font-medium text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Đang nộp...' : 'Nộp bài'}
                        </button>
                    )}
                </div>
            </div>

            {/* Question Navigation */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Chuyển đến câu hỏi:</h3>
                <div className="grid grid-cols-10 gap-2">
                    {quiz.questions.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentQuestion(index)}
                            className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                                index === currentQuestion
                                    ? 'bg-indigo-600 text-white'
                                    : isAnswered(index)
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            </div>

            {/* Confirm Submit Modal */}
            {showConfirmSubmit && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">Xác nhận nộp bài</h3>
                        <div className="space-y-3 mb-6">
                            <p className="text-slate-600">
                                Bạn có chắc chắn muốn nộp bài? Sau khi nộp bài, bạn không thể thay đổi câu trả lời.
                            </p>
                            <div className="bg-slate-50 rounded-lg p-3">
                                <div className="text-sm text-slate-700">
                                    <div>Tổng số câu: {quiz.questions.length}</div>
                                    <div>Đã trả lời: {getAnsweredCount()}</div>
                                    <div>Chưa trả lời: {quiz.questions.length - getAnsweredCount()}</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmSubmit(false)}
                                className="flex-1 px-4 py-2 rounded-xl font-medium text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                                Hủy
                            </button>
                            <button
                                onClick={() => handleSubmit(false)}
                                disabled={loading}
                                className="flex-1 px-4 py-2 rounded-xl font-medium text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                                {loading ? 'Đang nộp...' : 'Nộp bài'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizTaking;